import React, { useEffect, useState } from "react";
import { isIOS, isMobile, isTablet } from "react-device-detect";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";

import AccountInfo from "../AccountInfo";

import OtherBank from "assets/images/other-bank.png";

import { IbanInvoiceResponseDto } from "api";
import { Bank, banks } from "shared/constants/banks";
import { isInAppBrowser } from "shared/constants/isInAppBrowser";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

interface Props {
  iban: IbanInvoiceResponseDto | null;
  preferredBankName?: string;
  onBankClick?: (bank: Bank) => void;
  isPreview?: boolean;
}

const Banks: React.FC<Props> = (props) => {
  const { iban, preferredBankName, onBankClick, isPreview } = props;
  const [showAll, setShowAll] = useState(false);

  const sliceValue = showAll ? 5 : 4;

  const isBankAvailableOnCurrentPlatform = (bank: Bank) =>
    isIOS
      ? Boolean(bank.packageIOS || bank.universalLinkIOS)
      : Boolean(bank.packageAndroid);

  const preferredBank = banks.find((item) => item.name === preferredBankName);

  const platformBanks = banks.filter(isBankAvailableOnCurrentPlatform);

  const filteredBanks =
    preferredBank && isBankAvailableOnCurrentPlatform(preferredBank)
      ? Array.from(new Set([preferredBank, ...platformBanks]))
      : platformBanks;

  useEffect(() => {
    try {
      const { location } = window;

      const queryParams = new URLSearchParams(location.search);
      const debug = queryParams.get("debug") || "";

      if (debug === "true" && (isMobile || isTablet)) {
        const userAgent = navigator.userAgent.toLowerCase();

        alert(
          `Browser info: \n\nUser agent: ${userAgent}\n\nisInAppBrowser: ${isInAppBrowser()}`,
        );
      }
    } catch (e) {
      const error = e as Error;
      alert(`Error displaying browser info: ${error.message}.`);
    }
  }, []);

  const handleToggleShowAll = () => {
    !isPreview &&
      analytics().sendConversionEvent(
        AnalyticsEvents.iban_qr_other_banks,
        null,
        true,
      );

    setShowAll(!showAll);
  };

  return (
    <Box maxWidth={"312px"} margin="0 auto">
      <Grid container spacing={2}>
        {filteredBanks.slice(0, sliceValue).map((bank, index) => (
          <Grid item xs={2.4} sm={2.4} key={index}>
            <ButtonBase
              sx={{ width: "100%" }}
              disableRipple
              disableTouchRipple
              onClick={() => onBankClick && onBankClick(bank)}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                maxWidth="100%"
              >
                {bank.logo}
                <Typography
                  variant="subtitle2"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "inherit",
                    textAlign: "center",
                  }}
                  mt={1}
                >
                  {bank.name}
                </Typography>
              </Box>
            </ButtonBase>
          </Grid>
        ))}
        {!showAll && (
          <Grid item xs={2.4} sm={2.4}>
            <ButtonBase
              data-testid="other-banks-button"
              onClick={handleToggleShowAll}
              sx={{ width: "100%" }}
              disableRipple
              disableTouchRipple
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                maxWidth="100%"
              >
                <Box component="img" src={OtherBank} alt="Інші" />
                <Typography
                  variant="subtitle2"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "inherit",
                    textAlign: "center",
                  }}
                  mt={1}
                >
                  Інші
                </Typography>
              </Box>
            </ButtonBase>
          </Grid>
        )}
      </Grid>

      <Collapse in={showAll} timeout="auto" unmountOnExit>
        <Grid container spacing={2} sx={{ marginTop: 1 }}>
          {filteredBanks.slice(sliceValue).map((bank, index) => (
            <Grid item xs={2.4} sm={2.4} key={index + 4}>
              <ButtonBase
                sx={{ width: "100%" }}
                disableRipple
                disableTouchRipple
                onClick={() => onBankClick && onBankClick(bank)}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  maxWidth="100%"
                >
                  {bank.logo}
                  <Typography
                    variant="subtitle2"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "inherit",
                      textAlign: "center",
                    }}
                    mt={1}
                  >
                    {bank.name}
                  </Typography>
                </Box>
              </ButtonBase>
            </Grid>
          ))}
        </Grid>
        <Box pt={1.5} pb={1.5} textAlign="center">
          <Typography variant="subtitle2" color="grey700">
            Не знайшли вашого банку?
          </Typography>
          <Typography variant="subtitle2">
            Ви завжди можете перенести дані в ручну.
          </Typography>
        </Box>
        <AccountInfo iban={iban} copyEnabled={!isPreview} />
      </Collapse>
    </Box>
  );
};

export default Banks;
